import { Controller } from '@hotwired/stimulus';

export default class extends Controller {
  static targets = ['producerTemplate', 'producersContainer', 'auditedProducerTemplate', 'auditedProducersContainer', 'plantationTemplate', 'plantationsContainer'];
  static values = { piLineId: String, plantations: String };

  connect() {
    this.updateDropdowns();
  }

  // Afegeix una fila a productors no certificats clonant-ne la plantilla
  addProducer = (event) => {
    event.preventDefault();
    const button = event.currentTarget;

    if (button.dataset.disabled === 'true') return;

    const collapseContainer = button.closest('.collapse');
    const container = collapseContainer?.querySelector('.producers-container');
    const template = collapseContainer?.querySelector('template[data-ecc-audits-target="producerTemplate"]');

    if (container && template) {
      const timestamp = Date.now();
      const auditPiLineId = button.dataset.eccAuditPiLineId;

      const clone = template.content.cloneNode(true);
      this.updateAttributes(clone, timestamp);

      const selectElement = clone.querySelector('select');
      if (selectElement) {
        selectElement.addEventListener('change', this.handleSelectionChange);
      }

      if (auditPiLineId) {
        clone.appendChild(this.createHiddenInput(auditPiLineId, timestamp));
      }

      container.appendChild(clone);
      this.updateDropdowns();
    }
  };

  // Afegeix una fila al llistat de productors auditats de l'auditoria
  addAuditedProducer = (event) => {
    event.preventDefault();
    const button = event.currentTarget;

    if (button.dataset.disabled === 'true') return;

    const container = this.hasAuditedProducersContainerTarget
      ? this.auditedProducersContainerTarget
      : this.element.querySelector('.audited-producers-container');

    const template = this.hasAuditedProducerTemplateTarget
      ? this.auditedProducerTemplateTarget
      : this.element.querySelector('template[data-ecc-audits-target="auditedProducerTemplate"]');

    if (container && template) {
      const timestamp = Date.now();
      const clone = template.content.cloneNode(true);
      this.updateAttributes(clone, timestamp);

      const selectElement = clone.querySelector('select');
      if (selectElement) {
        selectElement.addEventListener('change', this.handleSelectionChange);
      }

      container.appendChild(clone);
      this.updateDropdowns();
    }
  };

  // Treu una fila de productor: marca _destroy si ja existeix a la BD, si no la esborra del DOM
  removeProducer = (event) => {
    event.preventDefault();
    const producerField = event.target.closest('.producer-field');

    if (producerField) {
      const destroyInput = producerField.querySelector('input[name*="_destroy"]');

      if (destroyInput) {
        destroyInput.value = '1';
        producerField.style.display = 'none';
      } else {
        producerField.remove();
      }

      this.updateDropdowns();
    }
  };

  // Afegeix una fila a plantacions no certificades clonant-ne la plantilla
  addPlantation = (event) => {
    event.preventDefault();
    const button = event.currentTarget;

    if (button.dataset.disabled === 'true') return;

    const collapseContainer = button.closest('.collapse');
    const container = collapseContainer?.querySelector('.plantations-container');
    const template = collapseContainer?.querySelector('template[data-ecc-audits-target="plantationTemplate"]');

    if (container && template) {
      const timestamp = Date.now();
      const auditPiLineId = button.dataset.eccAuditPiLineId;

      const clone = template.content.cloneNode(true);
      this.updateAttributes(clone, timestamp, 'new_plantation');

      const selectElement = clone.querySelector('select');
      if (selectElement) {
        selectElement.addEventListener('change', this.handleSelectionChange);
      }

      if (auditPiLineId) {
        clone.appendChild(this.createHiddenInput(auditPiLineId, timestamp));
      }

      container.appendChild(clone);
      this.updateDropdowns();
    }
  };

  // Treu una fila de plantació, amb el mateix criteri que removeProducer
  removePlantation = (event) => {
    event.preventDefault();
    const plantationField = event.target.closest('.plantation-field');

    if (plantationField) {
      const destroyInput = plantationField.querySelector('input[name*="_destroy"]');

      if (destroyInput) {
        destroyInput.value = '1';
        plantationField.style.display = 'none';
      } else {
        plantationField.remove();
      }

      this.updateDropdowns();
    }
  };

  // En triar productor, omple el desplegable de plantacions amb les seves
  onPlantationProducerChange = (event) => {
    const producerSelect = event.currentTarget;
    const row = producerSelect.closest('.plantation-field');
    if (!row) return;

    const plantationSelect = row.querySelector('.plantation-plantation-select');
    if (!plantationSelect) return;

    const producerId = producerSelect.value;
    plantationSelect.innerHTML = '';
    const blank = document.createElement('option');
    blank.value = '';
    blank.textContent = '';
    plantationSelect.appendChild(blank);

    if (!producerId) {
      plantationSelect.disabled = true;
      this.updateDropdowns();
      return;
    }

    const options = this.plantationsData()[producerId] || [];
    options.forEach((opt) => {
      const option = document.createElement('option');
      option.value = opt.id;
      option.textContent = opt.label;
      plantationSelect.appendChild(option);
    });
    plantationSelect.disabled = options.length === 0;
    this.updateDropdowns();
  };

  // Qualsevol canvi de selecció obliga a recalcular què pot sortir a la resta de desplegables
  handleSelectionChange = () => {
    this.updateDropdowns();
  };

  // Aplica totes les regles de visibilitat dels desplegables de la línia de PI
  updateDropdowns = () => {
    const lineSelected = this.piLineSelected();
    this.toggleExceptionFields(lineSelected);

    const producerSelects = this.activeSelects('.producer-field select[name*="[producer_id]"]');
    const plantationProducerSelects = this.activeSelects(
      '.plantation-field .plantation-producer-select',
    );

    const nonCertifiedProducerIds = this.selectedValues(producerSelects);
    const withNonCertifiedPlantationIds = this.selectedValues(plantationProducerSelects);

    this.hideTakenOptions(
      producerSelects,
      [...nonCertifiedProducerIds, ...withNonCertifiedPlantationIds],
    );

    this.hideTakenOptions(plantationProducerSelects, nonCertifiedProducerIds);
    this.hideTakenPlantations(plantationProducerSelects);

    this.updateAddButtons(nonCertifiedProducerIds, withNonCertifiedPlantationIds, lineSelected);
    this.updatePlantationAddButtons(nonCertifiedProducerIds, plantationProducerSelects.length,
                                    lineSelected);
  };

  // Sense la línia de PI marcada no es poden indicar excepcions
  piLineSelected = () => {
    const checkbox = this.element.querySelector('input[name="ecc_audit[pi_line_id][]"]');
    return !checkbox || checkbox.checked;
  };

  toggleExceptionFields = (enabled) => {
    this.element.querySelectorAll('.producer-field select, .plantation-field select')
      .forEach((select) => {
        if (enabled) {
          if (select.dataset.lineDisabled !== 'true') return;

          delete select.dataset.lineDisabled;
          select.disabled = false;
        } else if (!select.disabled) {
          select.dataset.lineDisabled = 'true';
          select.disabled = true;
        }
      });

    this.element.querySelectorAll('a[data-bs-toggle="collapse"]')
      .forEach((link) => link.classList.toggle('disabled', !enabled));
  };

  // Selects de la línia, descartant les files marcades per esborrar
  activeSelects = (selector) => [...this.element.querySelectorAll(selector)].filter((select) => {
    const destroyInput = select
      .closest('.producer-field, .plantation-field')
      ?.querySelector('input[name*="_destroy"]');
    return !destroyInput || destroyInput.value !== '1';
  });

  // Valors triats en un conjunt de selects, sense els buits
  selectedValues = (selects) => selects
    .map((select) => select.value)
    .filter((value) => value !== '');

  // Amaga les opcions ja ocupades, respectant sempre la que té triada cada select
  hideTakenOptions = (selects, takenValues) => {
    const taken = new Set(takenValues);

    selects.forEach((select) => {
      [...select.options].forEach((option) => {
        option.hidden = option.value !== '' && option.value !== select.value
          && taken.has(option.value);
      });
    });
  };

  // Dins de plantacions no certificades, cada plantació d'un productor només es pot triar un cop
  hideTakenPlantations = (plantationProducerSelects) => {
    const takenByProducer = new Map();

    plantationProducerSelects.forEach((producerSelect) => {
      const plantationSelect = this.plantationSelectFor(producerSelect);
      if (!producerSelect.value || !plantationSelect?.value) return;

      const taken = takenByProducer.get(producerSelect.value) || new Set();
      taken.add(plantationSelect.value);
      takenByProducer.set(producerSelect.value, taken);
    });

    plantationProducerSelects.forEach((producerSelect) => {
      const plantationSelect = this.plantationSelectFor(producerSelect);
      if (!plantationSelect) return;

      this.hideTakenOptions([plantationSelect], takenByProducer.get(producerSelect.value) || []);
    });
  };

  // Select de plantació que acompanya un select de productor dins la mateixa fila
  plantationSelectFor = (producerSelect) => producerSelect
    .closest('.plantation-field')
    ?.querySelector('.plantation-plantation-select');

  // Desactiva el botó + quan ja no queda cap combinació productor-plantació lliure
  updatePlantationAddButtons = (nonCertifiedProducerIds, selectedRows, lineSelected = true) => {
    const addButtons = this.element.querySelectorAll('.add-btn[data-action*="addPlantation"]');
    const excluded = new Set(nonCertifiedProducerIds);
    const availableCombinations = Object.entries(this.plantationsData())
      .filter(([producerId]) => !excluded.has(producerId))
      .reduce((total, [, plantations]) => total + plantations.length, 0);

    addButtons.forEach((button) => {
      const isDisabled = !lineSelected || selectedRows >= availableCombinations;

      button.dataset.disabled = isDisabled.toString();
      const addIcon = button.querySelector('.add-icon');
      if (addIcon) addIcon.classList.toggle('disabled-icon', isDisabled);
    });
  };

  plantationsData = () => {
    try {
      return this.hasPlantationsValue ? JSON.parse(this.plantationsValue) : {};
    } catch (_) {
      return {};
    }
  };

  // Desactiva el botó + de productors quan tots els disponibles ja estan en ús
  updateAddButtons = (nonCertifiedProducerIds, withNonCertifiedPlantationIds, lineSelected = true) => {
    const addButtons = this.element.querySelectorAll('.add-btn[data-action*="addProducer"]');
    const taken = new Set([...nonCertifiedProducerIds, ...withNonCertifiedPlantationIds]);

    addButtons.forEach((button) => {
      const container = button.closest('.collapse') || this.element;
      const allProducers = this.getProducers(container);

      const addIcon = button.querySelector('.add-icon');
      const isDisabled = !lineSelected
        || (allProducers.length > 0 && allProducers.every((producerId) => taken.has(producerId)));

      button.dataset.disabled = isDisabled.toString();
      if (addIcon) addIcon.classList.toggle('disabled-icon', isDisabled);
    });
  };

  // Tots els productors que ofereix el desplegable, estiguin triats o no
  getProducers = (container) => {
    const select = container.querySelector('.producer-field select[name*="[producer_id]"]');
    return select
      ? [...select.options].filter((option) => option.value !== '').map((option) => option.value)
      : [];
  };

  // Substitueix els tokens new_* del clon per un índex únic perquè Rails hi vegi una fila nova
  updateAttributes = (clone, timestamp, replaceToken = 'new_producer') => {
    const elements = clone.querySelectorAll('[name], [id], [for]');
    const tokens = replaceToken === 'new_plantation'
      ? ['new_pi_line', 'new_plantation']
      : ['new_pi_line', 'new_producer', 'new_audited_producer'];

    elements.forEach((element) => {
      ['name', 'id', 'for'].forEach((attr) => {
        if (element.hasAttribute(attr)) {
          let value = element.getAttribute(attr);
          tokens.forEach((token) => {
            value = value.replace(new RegExp(token, 'g'), timestamp);
          });
          element.setAttribute(attr, value);
        }
      });
    });
  };

  // Camp ocult que lliga la fila nova amb la línia de PI ja desada
  createHiddenInput = (auditPiLineId, timestamp) => {
    const hiddenInput = document.createElement('input');
    hiddenInput.type = 'hidden';
    hiddenInput.name = `ecc_audit[ecc_audit_pi_lines_attributes][${timestamp}][id]`;
    hiddenInput.value = auditPiLineId;
    return hiddenInput;
  };
}
